import { useMemo, useState } from "react";
import {
  AUDIT_DATUM,
  KDE_TO_JE,
  FTP_SOUBORY,
  CENIK_SLOUPCE,
  ARTICLE_STATUS,
  STOCK_AVAILABLE,
  MEASURE_UNIT,
  DISCOUNT_GROUP,
  PIM_VAZBY,
  PIM_TEXTY,
  PIM_JAZYKY,
  PASTI,
  NEPOUZIVAME,
  ZDROJE,
  ZIP_OBSAH,
  ZAMITNUTO,
  VADY,
  FTP_UPLNOST,
  ROZSAH,
  DRUHE_KOLO,
  type Radek,
  type Zdroj,
} from "./zdrojeData.js";

/**
 * „Zdroje KTM" — jedno místo, kde je popsané, odkud která informace pochází.
 *
 * Proč to existuje: tatáž informace bývá na víc místech a jinde znamená něco
 * jiného. Než se sáhne na data, patří sem.
 *
 * Stránka je záměrně STATICKÁ — je to dokumentace, ne dashboard. Data jsou
 * v `zdrojeData.ts`, kde je u každého tvrzení uvedeno, čím bylo ověřeno.
 */

const SEKCE = [
  { id: "postup", label: "Postup" },
  { id: "kde", label: "Co potřebuju → kde to je" },
  { id: "zdroje", label: "Zdroje" },
  { id: "ftp", label: "Soubory na FTP" },
  { id: "cenik", label: "Ceník — 24 sloupců" },
  { id: "ciselniky", label: "Číselníky" },
  { id: "pim", label: "PIM XML" },
  { id: "pasti", label: "Pasti" },
  { id: "zamitnuto", label: "Změřeno a zamítnuto" },
  { id: "vady", label: "Otevřené vady" },
  { id: "nepouzivame", label: "Máme a nepoužíváme" },
];

export function ZdrojePage(): JSX.Element {
  const [hledat, setHledat] = useState("");
  const q = hledat.trim().toLowerCase();

  const filtr = useMemo(
    () => (radky: Radek[]): Radek[] =>
      q
        ? radky.filter((r) =>
            [r.a, r.b, r.c, r.d].some((v) => (v ?? "").toLowerCase().includes(q)),
          )
        : radky,
    [q],
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900">Zdroje KTM</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Odkud která informace pochází — a kde naopak NENÍ. Poslední úplný audit{" "}
          <strong className="text-zinc-700">{AUDIT_DATUM}</strong> proti oficiální
          dokumentaci KTM, živému FTP a reálným datům.
        </p>
        <p className="mt-2 text-xs text-zinc-500">
          <span className="inline-block rounded bg-zinc-100 px-2 py-0.5 mr-2 font-medium text-zinc-600">
            Rozsah
          </span>
          {ROZSAH}. Ostatní značky a mimoevropské trhy neřešíme.
          <span className="mx-2 text-zinc-300">·</span>
          <span className="inline-block rounded bg-zinc-100 px-2 py-0.5 mr-2 font-medium text-zinc-600">
            Druhé kolo
          </span>
          {DRUHE_KOLO.potvrzeno} tvrzení přeměřeno druhou metodou a potvrzeno,{" "}
          {DRUHE_KOLO.chybnaMetoda}× se ukázala chybná ověřovací metoda (ne původní
          tvrzení).
        </p>
      </header>

      <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 mb-6">
        <p className="text-sm text-amber-900">
          <strong>Nejdražší past, jakou tu máme:</strong> tatáž informace je na víc
          místech a jinde znamená něco jiného. Než začneš počítat, najdi si údaj
          v tabulce „Co potřebuju → kde to je". A než si význam pole odvodíš
          z chování — <strong>hledej dokumentaci, máme ji od roku 2023.</strong>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <input
          type="search"
          value={hledat}
          onChange={(e) => setHledat(e.target.value)}
          placeholder="Hledat ve všech tabulkách — např. „hmotnost“, „ArticleStatus“, „fitment“…"
          className="flex-1 min-w-[280px] rounded-md border border-zinc-300 px-3 py-2 text-sm
                     focus:outline-none focus:ring-2 focus:ring-ktm-orange/40 focus:border-ktm-orange"
        />
        {q && (
          <button
            type="button"
            onClick={() => setHledat("")}
            className="text-sm text-zinc-500 hover:text-zinc-800 px-2 py-2"
          >
            zrušit
          </button>
        )}
      </div>

      <nav className="flex flex-wrap gap-1.5 mb-8 pb-4 border-b border-zinc-200">
        {SEKCE.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="text-xs px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-600
                       hover:bg-zinc-200 hover:text-zinc-900 transition-colors"
          >
            {s.label}
          </a>
        ))}
      </nav>

      {/* ---------------- POSTUP ---------------- */}
      <Sekce id="postup" titul="Postup, který se nesmí obejít">
        <div className="rounded-lg border border-zinc-200 bg-white p-5">
          <ol className="space-y-3 text-sm text-zinc-700">
            {[
              ["SOUPIS všech dostupných zdrojů", "Projet všechny — ne první, který funguje."],
              ["ZMĚŘENÍ, který je přesnější", "Na podmnožině, kde znám pravdu nezávisle."],
              ["VÝBĚR zdroje + doklad u zamítnutých", "Zamítnutý zdroj se dokládá měřením, ne odhadem."],
              ["Teprve TEĎ import nebo zápis", ""],
            ].map(([t, p], i) => (
              <li key={t} className="flex gap-3">
                <span className="flex-none w-6 h-6 rounded-full bg-ktm-orange/10 text-ktm-orange
                                 text-xs font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <span>
                  <strong className="text-zinc-900">{t}</strong>
                  {p && <span className="text-zinc-500"> — {p}</span>}
                </span>
              </li>
            ))}
          </ol>
          <p className="mt-4 pt-4 border-t border-zinc-100 text-sm text-zinc-600">
            Když je zdroj jedno velké textové pole nebo tabulka,{" "}
            <strong>projdi ho CELÝ</strong>, ne první sekci. A levné zdroje zkoušej,
            i když dodavatel tvrdí, že nic nemá.
          </p>
        </div>
      </Sekce>

      {/* ---------------- KDE TO JE ---------------- */}
      <Sekce
        id="kde"
        titul="Co potřebuju → kde to je (a kde to NENÍ)"
        popis="Nejdůležitější tabulka na téhle stránce. Sloupec „pozor“ je to, co nás stálo nejvíc času."
      >
        <Tabulka
          hlavicka={["Co hledám", "Primární zdroj", "Je i jinde", "Pozor"]}
          radky={filtr(KDE_TO_JE)}
        />
      </Sekce>

      {/* ---------------- ZDROJE ---------------- */}
      <Sekce id="zdroje" titul="Zdroje, které máme">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ZDROJE.filter(
            (z) =>
              !q ||
              [z.nazev, z.co, z.pristup, z.pozor].some((v) =>
                v.toLowerCase().includes(q),
              ),
          ).map((z) => (
            <ZdrojKarta key={z.id} z={z} />
          ))}
        </div>
      </Sekce>

      {/* ---------------- FTP ---------------- */}
      <Sekce
        id="ftp"
        titul="Co všechno leží na ftp1.ktm.com"
        popis={`Živý výpis ze ${AUDIT_DATUM}. Číslo v názvu určuje značku.`}
      >
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 mb-4 text-sm text-emerald-900">
          <strong>Tohle je úplný seznam, ne výběr.</strong> Kořen má{" "}
          {FTP_UPLNOST.koren} položek ({FTP_UPLNOST.korenSouboru} souborů + adresář
          /DEV), /DEV má {FTP_UPLNOST.dev}. Výpisy NLST i LIST se shodují a přímým
          dotazem na velikost bylo vyzkoušeno {FTP_UPLNOST.zkousenoVariant} kombinací
          názvů (značka × 00/35 × Y1/Z1 × CZK/EUR × EN/CS/DE/SK) —{" "}
          <strong>nenašel se ani jeden soubor navíc.</strong> Soubory z dokumentace,
          které tu NEJSOU: americké ceníky, replacement 0600/0650/0960, XML pro USA
          a fakturační PGA_Invoice.
        </div>
        <Tabulka
          hlavicka={["Soubor", "Značka / obsah", "Velikost", "Poznámka"]}
          radky={filtr(FTP_SOUBORY)}
          prvniMono
        />

        <div className="mt-6">
          <h3 className="text-sm font-semibold text-zinc-700 mb-2">
            Co je uvnitř PIM zipu — není to jen XML
          </h3>
          <Tabulka
            hlavicka={["Položka", "Co to je", "Kolik", "Poznámka"]}
            radky={filtr(ZIP_OBSAH)}
            prvniMono
          />
        </div>
      </Sekce>

      {/* ---------------- CENÍK ---------------- */}
      <Sekce
        id="cenik"
        titul="Ceník 0150 — všech 24 sloupců"
        popis="Vyplněnost změřená na všech 81 920 řádcích. Sloupec, který je vyplněný ze 3 %, se nedá použít jako podmínka."
      >
        <Tabulka
          hlavicka={["Sloupec", "Co to je", "Vyplněno", "Poznámka"]}
          radky={filtr(CENIK_SLOUPCE)}
          prvniMono
        />
      </Sekce>

      {/* ---------------- ČÍSELNÍKY ---------------- */}
      <Sekce id="ciselniky" titul="Číselníky od výrobce">
        <PodSekce titul="ArticleStatus — objednatelnost (NE dostupnost)">
          <Tabulka
            hlavicka={["Kód", "Aktivní", "Objednatelné", "Význam a počet v datech"]}
            radky={filtr(ARTICLE_STATUS)}
            prvniMono
          />
        </PodSekce>

        <PodSekce titul="StockAvailable — dostupnost u výrobce">
          <Tabulka
            hlavicka={["Kód", "Oficiální význam", "Česky", "Počet"]}
            radky={filtr(STOCK_AVAILABLE)}
            prvniMono
          />
        </PodSekce>

        <PodSekce titul="MeasureUnit — měrná jednotka">
          <Tabulka
            hlavicka={["Kód v datech", "Význam", "Počet", "Poznámka"]}
            radky={filtr(MEASURE_UNIT)}
            prvniMono
          />
        </PodSekce>

        <PodSekce titul="DiscountGroup — rabat (číselník, nepočítá se z cen)">
          <Tabulka
            hlavicka={["Kód", "Rabat", "Počet", "Poznámka"]}
            radky={filtr(DISCOUNT_GROUP)}
            prvniMono
          />
        </PodSekce>
      </Sekce>

      {/* ---------------- PIM ---------------- */}
      <Sekce
        id="pim"
        titul="PIM XML — co v něm doopravdy je"
        popis="Export „International“, verze 3218 ze 7. 8. 2026: 19 522 artiklů a 12 050 struktur (z toho 11 868 modelů) — tedy zdaleka ne všechny artikly z ceníku. ⚠ Verze je v hlavičce souboru (completeversion) a mění se denně — vždy ji uveď u každého čísla z XML."
      >
        <div className="rounded-lg border border-zinc-200 bg-white p-5 mb-4 text-sm text-zinc-700">
          <p className="mb-2">
            <strong className="text-zinc-900">Tři kořeny:</strong>{" "}
            <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">ROOT_STRUKTUREN</code>{" "}
            (hierarchie po model + texty a fotky vedené na modelu) ·{" "}
            <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">ROOT_PRODUKTE</code>{" "}
            (artikly) ·{" "}
            <code className="text-xs bg-zinc-100 px-1 py-0.5 rounded">ROOT_STAMMDATEN</code>{" "}
            (číselníky — 762 atributů, 26 tříd, 33 měrných jednotek).
          </p>
          <p className="text-red-700">
            <strong>Klíčové:</strong> informace je vedená na úrovni MODELU i ARTIKLU.
            Kdo čte jen artikly, přijde o popisy a fotky — a přesně to se nám stalo.
          </p>
        </div>

        <PodSekce titul="Texty — na které úrovni je co">
          <Tabulka
            hlavicka={["TEXTART", "Co to je", "Kolik toho je", "Poznámka"]}
            radky={filtr(PIM_TEXTY)}
            prvniMono
          />
        </PodSekce>

        <PodSekce titul="Jazyky v exportu">
          <Tabulka
            hlavicka={["Jazyk", "Počet textů (artikly · modely)", "Poznámka", ""]}
            radky={filtr(PIM_JAZYKY)}
          />
        </PodSekce>

        <PodSekce titul="Vazby mezi artikly (BEZIEHUNGEN)">
          <Tabulka
            hlavicka={["Typ vazby", "Co znamená", "Kolik toho je", "Stav u nás"]}
            radky={filtr(PIM_VAZBY)}
            prvniMono
          />
        </PodSekce>
      </Sekce>

      {/* ---------------- PASTI ---------------- */}
      <Sekce
        id="pasti"
        titul="Na čem jsme se spálili"
        popis="Každá položka je skutečný případ, ne teorie. Kdo si to přečte, ušetří si den."
      >
        <div className="space-y-3">
          {filtr(PASTI).map((p) => (
            <div
              key={p.a}
              className={`rounded-lg border p-4 ${
                p.zvyraznit === "cerveny"
                  ? "border-red-200 bg-red-50"
                  : "border-amber-200 bg-amber-50"
              }`}
            >
              <h4 className="font-semibold text-sm text-zinc-900">{p.a}</h4>
              <p className="mt-1 text-sm text-zinc-700">{p.b}</p>
              {p.c && <p className="mt-1.5 text-sm text-zinc-600">{p.c}</p>}
              {p.d && (
                <p className="mt-2 text-xs font-medium text-zinc-800">→ {p.d}</p>
              )}
            </div>
          ))}
        </div>
      </Sekce>

      {/* ---------------- ZAMÍTNUTO ---------------- */}
      <Sekce
        id="zamitnuto"
        titul="Změřeno a zamítnuto"
        popis="Nápady, které vypadaly slibně a měřením neobstály. Jsou tu proto, aby je za měsíc nikdo nezkoušel znovu."
      >
        <Tabulka
          hlavicka={["Co se zkoušelo", "Proč to vypadalo dobře", "Co ukázalo měření", "Závěr"]}
          radky={filtr(ZAMITNUTO)}
        />
      </Sekce>

      {/* ---------------- VADY ---------------- */}
      <Sekce
        id="vady"
        titul="Otevřené vady"
        popis="Víme o nich, opravené nejsou."
      >
        <div className="space-y-3">
          {filtr(VADY).map((v) => (
            <div
              key={v.a}
              className={`rounded-lg border p-4 ${
                v.zvyraznit === "cerveny"
                  ? "border-red-200 bg-red-50"
                  : "border-amber-200 bg-amber-50"
              }`}
            >
              <h4 className="font-semibold text-sm text-zinc-900">{v.a}</h4>
              <p className="mt-1 text-sm text-zinc-700">{v.b}</p>
              {v.c && <p className="mt-1.5 text-sm text-zinc-600">{v.c}</p>}
              {v.d && (
                <p className="mt-2 text-xs font-medium text-zinc-800">→ {v.d}</p>
              )}
            </div>
          ))}
        </div>
      </Sekce>

      {/* ---------------- NEPOUŽÍVÁME ---------------- */}
      <Sekce
        id="nepouzivame"
        titul="Co od výrobce máme a nepoužíváme"
        popis="Data, za která nic neplatíme a leží ladem."
      >
        <Tabulka
          hlavicka={["Co", "Kolik toho je", "Kde to leží", "K čemu by to bylo"]}
          radky={filtr(NEPOUZIVAME)}
        />
      </Sekce>

      <footer className="mt-12 pt-6 border-t border-zinc-200 text-xs text-zinc-400">
        Kdo přidá zdroj nebo najde past, dopíše to sem —{" "}
        <code className="bg-zinc-100 px-1 py-0.5 rounded">
          apps/internal-app/src/pages/admin/zdrojeData.ts
        </code>
        . U každého tvrzení uveď, čím bylo ověřeno.
      </footer>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Sekce({
  id,
  titul,
  popis,
  children,
}: {
  id: string;
  titul: string;
  popis?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <section id={id} className="mb-12 scroll-mt-6">
      <h2 className="text-lg font-semibold text-zinc-900 mb-1">{titul}</h2>
      {popis && <p className="text-sm text-zinc-500 mb-4">{popis}</p>}
      {!popis && <div className="mb-4" />}
      {children}
    </section>
  );
}

function PodSekce({
  titul,
  children,
}: {
  titul: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-zinc-700 mb-2">{titul}</h3>
      {children}
    </div>
  );
}

function Tabulka({
  hlavicka,
  radky,
  prvniMono,
}: {
  hlavicka: string[];
  radky: Radek[];
  prvniMono?: boolean;
}): JSX.Element {
  if (radky.length === 0) {
    return (
      <p className="text-sm text-zinc-400 italic py-3">
        Nic neodpovídá hledání.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50">
            {hlavicka.map((h) => (
              <th
                key={h}
                className="text-left font-medium text-zinc-500 text-xs uppercase
                           tracking-wide px-3 py-2"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {radky.map((r, i) => (
            <tr
              key={`${r.a}-${i}`}
              className={`border-b border-zinc-100 last:border-0 align-top ${
                r.zvyraznit === "cerveny"
                  ? "bg-red-50/60"
                  : r.zvyraznit === "zluty"
                    ? "bg-amber-50/60"
                    : r.zvyraznit === "zeleny"
                      ? "bg-emerald-50/60"
                      : ""
              }`}
            >
              <td
                className={`px-3 py-2.5 font-medium text-zinc-900 whitespace-nowrap ${
                  prvniMono ? "font-mono text-xs" : ""
                }`}
              >
                {r.a}
              </td>
              <td className="px-3 py-2.5 text-zinc-700">{r.b}</td>
              <td className="px-3 py-2.5 text-zinc-600">{r.c ?? ""}</td>
              <td className="px-3 py-2.5 text-zinc-600">{r.d ?? ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ZdrojKarta({ z }: { z: Zdroj }): JSX.Element {
  const barva =
    z.stav === "hlavní"
      ? "bg-emerald-100 text-emerald-800"
      : z.stav === "doplňkový"
        ? "bg-zinc-100 text-zinc-600"
        : "bg-amber-100 text-amber-800";

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-semibold text-zinc-900">{z.nazev}</h3>
        <span className={`flex-none text-xs px-2 py-0.5 rounded-full ${barva}`}>
          {z.stav}
        </span>
      </div>
      <p className="text-sm text-zinc-700 mb-3">{z.co}</p>
      <dl className="space-y-1 text-xs">
        <Radka popisek="Přístup" hodnota={z.pristup} />
        <Radka popisek="Frekvence" hodnota={z.frekvence} />
        <Radka popisek="Pozor" hodnota={z.pozor} duraz />
      </dl>
    </div>
  );
}

function Radka({
  popisek,
  hodnota,
  duraz,
}: {
  popisek: string;
  hodnota: string;
  duraz?: boolean;
}): JSX.Element {
  return (
    <div className="flex gap-2">
      <dt className="flex-none w-20 text-zinc-400">{popisek}</dt>
      <dd className={duraz ? "text-red-700 font-medium" : "text-zinc-600"}>
        {hodnota}
      </dd>
    </div>
  );
}
